<script>
	/*
	 * Extract Subtitles (desktop, Pro): reads the closed captions a video already carries
	 * with the bundled cc-embed tool and imports each track as its own Event Group.
	 * - 608 CC1-CC4 and 708 services 1-6 come out as MCC and decode through cc-lib's MacCaption profile;
	 * - OP-47 teletext (MXF) comes out as EBU STL and decodes through the EBU STL profile.
	 * Event times are relative to the video's first frame (decode incode = its start timecode),
	 * the same convention as project media.
	 */
	import { onMount } from "svelte";
	import { fade } from "svelte/transition";
	import { toast } from "@zerodevx/svelte-toast";
	import Swal from "sweetalert2";

	import { modalState, hideModal, showModal } from "@app/store/modalStore.svelte.js";
	import { styleState } from "@app/store/styleStore.svelte.js";
	import { insertHistoryAction } from "@app/store/historyStore.svelte.js";
	import { eventGroupState } from "@app/store/eventGroupStore.svelte.js";
	import { projectState } from "@app/store/projectStore.svelte.js";
	import { setMediaImportSeed } from "@app/store/mediaImportStore.svelte.js";
	import { SWAL_BASE, SWAL_CLASSES } from "@app/lib/swalClasses.js";
	import LanguageSelect from "@app/components/inputs/LanguageSelect.svelte";
	import * as m from "@app/paraglide/messages.js";

	/* CC LIB */
	import _EventGroup from "@cclib/classes/eventGroup.js";
	import bufferToHex from "@cclib/functions/utility/bufferToHex.js";
	import incodeTime from "@cclib/lib/incodeTime.js";
	import { cloneDecodeOptions, buildDecodeOptions, decodeSubtitleSource, stripBlankEvents, hasOverlaps, applyOverlapFix, importSpeakersFromGroup } from "@app/lib/subtitleDecodePipeline.js";
	import { probeMedia, extractCaptions, cancelCcEmbedJob, newCcEmbedJobId, describeCcEmbedError, getCcEmbedVersion } from "@app/lib/ccEmbed.js";
	import { scanMccTracks } from "@app/lib/mccTracks.js";

	const VIDEO_EXTENSIONS = ".mov,.mp4,.m4v,.mxf,.ts,.m2ts,.mts,.mpg,.mpeg,.m2p,.vob";
	const MCC_PROFILE = "MacCaption 608/708";
	const STL_PROFILE = "EBU STL";

	let eventGroupDefaults = JSON.parse(localStorage.getItem("cc-event-group-defaults")) || {};
	/* Per track id: the extract result and the decoded event group. Kept out of $state so
	   large MCC text and event arrays aren't wrapped in proxies. */
	const resultByTrack = new Map();
	const decodedByTrack = new Map();

	/* "select" -> "probing" -> "ready" -> "extracting" -> "review" */
	let step = $state("select"),
		videoFile = $state.raw(null),
		inputPath = $state(""),
		videoName = $state(""),
		probe = $state(null),
		jobId = $state(null),
		percent = $state(0),
		summary = $state(null),
		tracks = $state([]),
		teletextPage = $state("801"),
		loadAsMedia = $state(false),
		importing = $state(false),
		toolVersion = $state(null),
		errorText = $state("");

	let hasProjectMedia = $derived(Boolean(projectState.media?.localPath));
	let isProjectMedia = $derived(Boolean(inputPath && projectState.media?.localPath === inputPath));
	let probeHasOp47 = $derived(Boolean(probe?.captions?.some((c) => c.payload === "op47_sdp")));
	let probeHas608708 = $derived(Boolean(probe?.captions?.some((c) => c.payload !== "op47_sdp")));
	let selectedCount = $derived(tracks.filter((t) => t.selected && !t.disabled).length);

	onMount(async () => {
		toolVersion = await getCcEmbedVersion();
		// Start from the project's own video when it has one.
		if (hasProjectMedia) {
			useVideo(projectState.media.localPath, projectState.media.name || baseName(projectState.media.localPath));
		}
	});

	function baseName(filePath) {
		return (filePath || "").split(/[\\/]/).pop();
	}

	function stripExt(name) {
		const parts = name.split(".");
		if (parts.length > 1) parts.pop();
		return parts.join(".");
	}

	function tcLabel(tc) {
		if (!tc) return "00:00:00:00";
		const pad = (n) => String(n).padStart(2, "0");
		return `${pad(tc.hours)}:${pad(tc.minutes)}:${pad(tc.seconds)}${tc.drop_frame ? ";" : ":"}${pad(tc.frames)}`;
	}

	function reset() {
		resultByTrack.clear();
		decodedByTrack.clear();
		probe = null;
		summary = null;
		tracks = [];
		percent = 0;
		errorText = "";
	}

	async function handleFileInput(event) {
		const file = event.currentTarget.files?.[0];
		if (!file) return;
		videoFile = file;
		await useVideo(await window.webUtils.getPathForFile(file), file.name);
	}

	async function useVideo(path, name) {
		reset();
		inputPath = path;
		videoName = name;
		loadAsMedia = !hasProjectMedia;
		step = "probing";
		try {
			probe = await probeMedia(inputPath);
			step = "ready";
		} catch (err) {
			errorText = describeCcEmbedError(err);
			step = "select";
		}
	}

	function onProgress(event) {
		if (event.event === "progress" && typeof event.percent === "number") {
			percent = event.percent;
		}
	}

	async function cancelExtract() {
		if (jobId) await cancelCcEmbedJob(jobId);
	}

	async function runExtract(options) {
		jobId = newCcEmbedJobId();
		percent = 0;
		try {
			return await extractCaptions({ ...options, jobId, input: inputPath }, onProgress);
		} finally {
			jobId = null;
		}
	}

	async function extract() {
		resultByTrack.clear();
		decodedByTrack.clear();
		errorText = "";
		step = "extracting";
		try {
			const results = [];
			// 608/708 when present (else OP-47, which extract picks on its own) ...
			results.push(await runExtract({}));
			// ... and OP-47 too when the file carries both.
			if (results[0].summary.format === "mcc" && probeHasOp47) {
				try {
					results.push(await runExtract({ teletextPage }));
				} catch (err) {
					if (err.code === "cancelled") throw err;
					toast.push(describeCcEmbedError(err), { classes: ["toast-warning"] });
				}
			}
			summary = results[0].summary;
			tracks = await findTracks(results);
			step = "review";
			if (results.some((r) => r.summary.salvaged)) {
				toast.push(m.subtitle_extract_salvaged_toast(), { classes: ["toast-warning"], duration: 10000 });
			}
		} catch (err) {
			errorText = err.code === "cancelled" ? "" : describeCcEmbedError(err);
			step = "ready";
		}
	}

	function decodeWindow() {
		return {
			width: styleState.width,
			height: styleState.height,
			xPadding: styleState.xPadding,
			yPadding: styleState.yPadding,
		};
	}

	/* Decodes one track of an extract result into an event group. */
	async function decodeTrack(track) {
		const { summary: trackSummary, data } = resultByTrack.get(track.id);
		const profile = trackSummary.format === "stl" ? STL_PROFILE : MCC_PROFILE;
		const formatOptions = cloneDecodeOptions(profile);
		if (track.channel) {
			formatOptions.find((option) => option.name === "Channel").selected = track.channel;
		}
		const options = buildDecodeOptions({
			srcProfile: profile,
			srcFrameRate: trackSummary.info.frame_rate,
			srcDropFrame: trackSummary.info.drop_frame,
			srcIncode: tcLabel(trackSummary.info.start_timecode),
			srcUseClockTime: "yes",
			formatOptions: formatOptions,
			window: decodeWindow(),
		});
		const contents = trackSummary.format === "stl" ? bufferToHex(data) : data;
		return stripBlankEvents(await decodeSubtitleSource(contents, options));
	}

	/* The tracks found in the extract results, each decoded once to count its events. */
	async function findTracks(results) {
		const base = stripExt(videoName);
		const language = eventGroupDefaults.language || "en-US";
		const found = [];

		for (const result of results) {
			if (result.summary.format === "stl") {
				const page = result.summary.teletext_page || teletextPage;
				found.push({ id: `op47-${page}`, label: m.subtitle_extract_track_op47({ page }), result, channel: null, name: `${base} - ${page}` });
				continue;
			}
			const scan = scanMccTracks(result.data);
			scan.cea608.forEach(({ channel }) => {
				found.push({ id: channel, label: m.subtitle_extract_track_608({ channel }), result, channel: "ch0" + channel.slice(2), name: `${base} - ${channel}` });
			});
			// cc-lib's Channel option selects 708 services 1-6 (programA-programF).
			const services708 = scan.cea708.filter(({ service }) => service <= 6);
			services708.forEach(({ service }) => {
				found.push({
					id: `708-${service}`,
					label: m.subtitle_extract_track_708({ service }),
					result,
					channel: "program" + String.fromCharCode(64 + service),
					name: services708.length === 1 ? `${base} - 708` : `${base} - 708 Service ${service}`,
				});
			});
		}

		for (const track of found) {
			resultByTrack.set(track.id, track.result);
			delete track.result;
			track.language = language;
			track.rtl = eventGroupDefaults.rtl ?? false;
			if (track.disabled) {
				track.selected = false;
				continue;
			}
			try {
				const decoded = await decodeTrack(track);
				decodedByTrack.set(track.id, decoded);
				track.eventCount = decoded.events.length;
				track.selected = track.eventCount > 0;
				track.disabled = track.eventCount === 0;
				if (track.disabled) track.note = m.subtitle_extract_track_empty();
			} catch (err) {
				console.error("Decoding", track.id, "failed:", err);
				track.selected = false;
				track.disabled = true;
				track.note = m.subtitle_import_failed_toast({ error: err.message });
			}
		}
		return found;
	}

	function createEventGroup(track) {
		eventGroupState.push({
			...new _EventGroup({
				type: "subtitle",
				name: track.name,
				language: track.language,
				rtl: track.rtl,
				maxLines: eventGroupDefaults.maxLines || 2,
				maxChars: eventGroupDefaults.maxChars || 32,
				minDuration: eventGroupDefaults.minDuration || 0.5,
				maxDuration: eventGroupDefaults.maxDuration || 12,
				overlap: eventGroupDefaults.overlap,
				fontFamily: eventGroupDefaults.fontFamily ?? "plexMono",
				fontSize: eventGroupDefaults.fontSize || 16,
			}),
		});
		return eventGroupState.length - 1;
	}

	async function confirm(title, text) {
		const res = await Swal.fire({
			...SWAL_BASE,
			customClass: SWAL_CLASSES.confirmCancelLight,
			title,
			text,
			icon: "question",
			showCancelButton: true,
			confirmButtonText: m.yes(),
			cancelButtonText: m.no(),
			allowOutsideClick: false,
			allowEscapeKey: false,
		});
		return res.isConfirmed;
	}

	async function importTracks() {
		const chosen = tracks.filter((t) => t.selected && !t.disabled && decodedByTrack.has(t.id));
		if (chosen.length === 0) return;
		importing = true;
		try {
			// The video's start timecode becomes the project incode, so timecodes read as they do in the file.
			const start = summary.info.start_timecode;
			const startLabel = tcLabel(start);
			if (start && startLabel !== "00:00:00:00" && startLabel !== "00:00:00;00" && !(projectState.incode > 0) && (isProjectMedia || !hasProjectMedia)) {
				if (await confirm(m.subtitle_extract_incode_title(), m.media_import_update_incode_prompt({ timecode: startLabel }))) {
					projectState.incode = incodeTime.incodeLabelToMediaSeconds(startLabel, projectState.frameRate, projectState.dropFrame);
				}
			}

			let fixOverlaps;
			let firstIndex = null;
			for (const track of chosen) {
				let decoded = decodedByTrack.get(track.id);
				if (hasOverlaps(decoded)) {
					if (fixOverlaps === undefined) {
						fixOverlaps = await confirm(m.subtitle_import_overlap_title(), m.subtitle_import_overlap_text());
					}
					if (fixOverlaps) decoded = applyOverlapFix(decoded);
				}
				importSpeakersFromGroup(decoded);
				const index = createEventGroup(track);
				eventGroupState[index].events = decoded.events;
				firstIndex ??= index;
			}
			projectState.selected = firstIndex;

			insertHistoryAction({
				name: "extract subtitles",
				eventGroup: projectState.selected,
				snapshots: [
					{
						store: "eventGroupState",
						value: JSON.stringify(eventGroupState),
					},
				],
			});
			toast.push(m.subtitle_extract_done_toast({ count: chosen.length }), { classes: ["toast-success"] });

			if (loadAsMedia && videoFile) {
				// The Media Import modal loads the video the usual way (peaks, frame rate, incode).
				setMediaImportSeed({ mediaSource: "Local Storage", files: [videoFile] });
				showModal("mediaImport");
			} else {
				hideModal();
			}
		} catch (err) {
			console.error(err);
			toast.push(m.subtitle_import_failed_toast({ error: err.message }), { classes: ["toast-danger"] });
		} finally {
			importing = false;
		}
	}

	function close() {
		if (jobId) cancelExtract();
		hideModal();
	}
</script>

<div transition:fade={{ duration: 100 }}
	class="modal {modalState.selected === 'subtitleExtract' ? 'show d-block' : ''}"
	role="dialog"
	aria-labelledby="subtitleExtractTitle"
	aria-modal="true"
	tabindex="-1"
	id="SubtitleExtractModal">
	<div class="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable" role="document">
		<div class="modal-content">
			<div class="modal-header">
				<h4 class="modal-title" id="subtitleExtractTitle">{m.subtitle_extract_title()}</h4>
				<button type="button" class="btn-close" aria-label={m.close()} onclick={close}></button>
			</div>
			<div class="modal-body">
				<p class="small text-muted">
					<i class="bi bi-cpu"></i> {m.subtitle_extract_intro()}
					{#if toolVersion}<span class="ms-1">({toolVersion})</span>{/if}
				</p>

				<!-- Video -->
				<div class="mb-3 shadow bg-secondary rounded p-3">
					<label class="form-label" for="extractVideoPicker">{m.subtitle_extract_select_video_label()}</label>
					<input type="file" class="form-control" id="extractVideoPicker" accept={VIDEO_EXTENSIONS} onchange={handleFileInput} disabled={step === "probing" || step === "extracting" || importing} />
					{#if inputPath}
						<p class="small mt-2 mb-0 text-truncate" title={inputPath}>
							<i class="bi bi-film"></i> {videoName}
							{#if isProjectMedia}<span class="badge bg-info ms-1">{m.subtitle_extract_project_media_badge()}</span>{/if}
						</p>
					{/if}
				</div>

				{#if errorText}
					<div class="alert alert-danger" role="alert"><i class="bi bi-exclamation-triangle"></i> {errorText}</div>
				{/if}

				{#if step === "probing"}
					<p class="text-muted"><span class="spinner-border spinner-border-sm me-2" aria-hidden="true"></span>{m.subtitle_extract_probing()}</p>
				{/if}

				<!-- What the file carries -->
				{#if probe && (step === "ready" || step === "extracting")}
					<div class="mb-3 p-3 rounded border border-secondary">
						<h5 class="lead text-warning">{m.subtitle_extract_found_title()}</h5>
						<p class="small mb-1">
							{m.subtitle_extract_container_label()}: <strong>{probe.container?.format?.toUpperCase()}</strong>
							&middot; {m.subtitle_extract_start_tc_label()}: <strong>{probe.start_timecode ? tcLabel(probe.start_timecode) : "00:00:00:00"}</strong>
						</p>
						{#if probe.captions?.length}
							<ul class="small mb-0">
								{#each probe.captions as caption, i (i)}
									<li>{caption.location}</li>
								{/each}
							</ul>
						{:else}
							<p class="small text-warning mb-0"><i class="bi bi-exclamation-circle"></i> {m.subtitle_extract_none_found()}</p>
						{/if}
						{#if probeHasOp47 && probeHas608708}
							<div class="mt-2 col-4">
								<label class="form-label small" for="extractTeletextPage">{m.subtitle_extract_teletext_page_label()}</label>
								<input id="extractTeletextPage" class="form-control form-control-sm" type="text" bind:value={teletextPage} />
							</div>
						{/if}
					</div>
				{/if}

				{#if step === "extracting"}
					<div class="mb-3">
						<p class="small mb-1">{m.subtitle_extract_extracting()}</p>
						<div class="progress" role="progressbar" aria-label={m.subtitle_extract_extracting()} aria-valuenow={percent} aria-valuemin="0" aria-valuemax="100">
							<div class="progress-bar progress-bar-striped progress-bar-animated" style="width: {percent}%">{Math.floor(percent)}%</div>
						</div>
					</div>
				{/if}

				<!-- Tracks to import -->
				{#if step === "review"}
					{#if summary?.salvaged}
						<div class="alert alert-warning small" role="alert">
							<i class="bi bi-exclamation-triangle"></i> {m.subtitle_extract_salvaged_note({ detail: summary.input_error?.message || "" })}
						</div>
					{/if}
					{#if tracks.length === 0}
						<p class="text-warning">{m.subtitle_extract_no_tracks()}</p>
					{:else}
						<h5 class="lead text-warning">{m.subtitle_extract_tracks_title()}</h5>
						{#each tracks as track (track.id)}
							<div class="mb-2 p-2 rounded border border-secondary {track.disabled ? 'opacity-50' : ''}">
								<div class="form-check">
									<input class="form-check-input" type="checkbox" id="track-{track.id}" bind:checked={track.selected} disabled={track.disabled} />
									<label class="form-check-label" for="track-{track.id}">
										<strong>{track.label}</strong>
										{#if track.eventCount !== undefined}<span class="text-muted small ms-1">{m.subtitle_extract_event_count({ count: track.eventCount })}</span>{/if}
									</label>
								</div>
								{#if track.note}<p class="small text-muted mb-1 ms-4">{track.note}</p>{/if}
								{#if track.selected && !track.disabled}
									<div class="row g-2 ms-3 mt-1">
										<div class="col-6">
											<label class="form-label small" for="name-{track.id}">{m.subtitle_extract_group_name_label()}</label>
											<input id="name-{track.id}" class="form-control form-control-sm" type="text" bind:value={track.name} />
										</div>
										<div class="col-6">
											<label class="form-label small" for="lang-{track.id}">{m.subtitle_import_group_language_label()}</label>
											<LanguageSelect id="lang-{track.id}" class="form-control form-control-sm" bind:value={track.language} />
										</div>
									</div>
								{/if}
							</div>
						{/each}
						{#if videoFile && !hasProjectMedia}
							<div class="form-check form-switch mt-3">
								<input type="checkbox" class="form-check-input" id="extractLoadAsMedia" bind:checked={loadAsMedia} />
								<label class="form-check-label" for="extractLoadAsMedia">{m.subtitle_extract_load_media_label()}</label>
							</div>
						{/if}
					{/if}
				{/if}
			</div>
			<div class="modal-footer">
				{#if step === "extracting"}
					<button class="btn btn-outline-danger" type="button" onclick={cancelExtract}>{m.subtitle_extract_cancel()}</button>
				{:else if step === "review"}
					<button class="btn btn-outline-secondary" type="button" onclick={() => (step = "ready")} disabled={importing}>{m.subtitle_extract_back()}</button>
					<button class="btn btn-primary" type="button" onclick={importTracks} disabled={selectedCount === 0 || importing}>
						{m.subtitle_extract_import_action({ count: selectedCount })}
					</button>
				{:else}
					<button class="btn btn-primary" type="button" onclick={extract} disabled={step !== "ready"}>{m.subtitle_extract_action()}</button>
				{/if}
			</div>
		</div>
	</div>
</div>
